

import React, { useState, useEffect } from "react";
import { Modal, Select, Checkbox, Spin, Typography, Input } from "antd";

const { Option } = Select;

export interface DeleteFileModalProps {
  visible: boolean;
  onOk: (reason: string, permanent: boolean) => void;
  onCancel: () => void;
  loading: boolean;
  fileCount: number;
  spaceType?: string; // GxP flag to determine if space is Non-GxP
}

const DELETE_REASONS = [
  { value: "Operational-Correction-of-data-by-vendor", label: "Operational - Correction of data by vendor" },
  { value: "Operational-Correction-incomplete-or-corrupt-upload", label: "Operational - Correction - incomplete or corrupt upload" },
  { value: "Operational-Correction-file-uploaded-to-wrong-location", label: "Operational - Correction - file uploaded to wrong location" },
  { value: "Operational-Other", label: "Operational - Other" },
  { value: "Compliance-withdrawal-of-patient-consent", label: "Compliance - Withdrawal of patient consent" },
  { value: "Compliance-loss-off-rights-to-data", label: "Compliance - Loss of rights to data" },
  { value: "Compliance-life-cycle-expiration", label: "Compliance - Life cycle expiration" },
  { value: "Compliance-Other", label: "Compliance - Other" },
];

const DeleteFileModal: React.FC<DeleteFileModalProps> = ({
  visible,
  onOk,
  onCancel,
  loading,
  fileCount,
  spaceType,
}) => {
  const [reason, setReason] = useState<string>("");
  const [permanentDelete, setPermanentDelete] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  // Check if this is a Non-GxP space
  const isNonGxpSpace = spaceType?.toLowerCase() === "nongxp" || spaceType?.toLowerCase() === "non-gxp";
  const isOtherReason = reason.endsWith("-Other");

  useEffect(() => {
    if (!visible) {
      setReason("");
      setPermanentDelete(false);
      setError("");
      setOtherReason("");
    }
  }, [visible]);
  const handleOk = () => {
    // For Non-GxP spaces, we don't require a deletion reason
    if (isNonGxpSpace || reason) {
      if (!isNonGxpSpace && isOtherReason && otherReason.trim().length < 10) {
        setError("Please provide at least 10 characters for the 'Other' reason.");
        return;
      }
      setError("");
      // For Non-GxP spaces, use default values that won't be sent to API
      const reasonToUse = isNonGxpSpace
        ? "not-applicable-for-non-gxp"
        : isOtherReason
          ? otherReason.trim()
          : reason;
      const permanentDeleteToUse = isNonGxpSpace ? false : permanentDelete;
      onOk(reasonToUse, permanentDeleteToUse);
    } else {
      setError("Please select a reason for deletion.");
    }
  };

  const handleCancel = () => {
    setReason("");
    setPermanentDelete(false);
    setError("");
    setOtherReason("");
    onCancel();
  };

  return (
    <Modal
      title={"Delete Files/Folders"}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Delete"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{
        disabled: !isNonGxpSpace && (!reason || (isOtherReason && otherReason.trim().length < 10)),
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Typography.Title level={5} style={{ marginBottom: 0, color: "#3c3c3c", fontWeight: 600 }}>
            {isNonGxpSpace
              ? "Do you want to proceed with deletion?"
              : "Select Reason for Deletion:"}
        </Typography.Title>
        {/* Only show reason selection for GxP spaces */}
        {!isNonGxpSpace && (
          <>
            <Select
              style={{ width: "100%", marginTop: 6 }}
              value={reason}
              onChange={(val) => { setReason(val); setOtherReason(""); setError(""); }}
              placeholder="Please select a reason for deletion"
            >
              {DELETE_REASONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {isOtherReason && (
              <>
                <Input
                  style={{ marginTop: 8 }}
                  placeholder="Please describe the reason (minimum 10 characters)"
                  value={otherReason}
                  onChange={(e) => { setOtherReason(e.target.value); setError(""); }}
                  maxLength={500}
                />
              </>
            )}
            {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
          </>
        )}
      </div>
      {/* Only show permanent delete checkbox for GxP spaces */}
      {!isNonGxpSpace && (
        <>
          <Checkbox
            checked={permanentDelete}
            onChange={e => setPermanentDelete(e.target.checked)}
          >
            Permanent Delete
          </Checkbox>
          <div style={{ color: "red", marginTop: 10, fontSize: 12 }}>
            Warning: Objects deleted permanently will not be recoverable.
          </div>
        </>
      )}
      {loading && <Spin style={{ marginTop: 10 }} />}
    </Modal>
  );
};

export default DeleteFileModal;
